import { put, all, takeLatest, select } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as appActionTypes from "../auth/actionTypes";
import API from "../../commonUtils/API.js";
import { toast } from "react-toastify";
import { handleApiError } from "../../commonUtils/Functions";

function* getRolesSaga({ params }) {
    const { currentPage, perPage } = params;
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const filters = yield select((state) => state.Roles.filters);

        let queryString = `currentPage=${currentPage}&perPage=${perPage}`;
        if(filters.keyword) queryString = queryString.concat(`&keyword=${encodeURIComponent(filters.keyword)}`);

        const { data } = yield API.get(`/roles?${queryString}`);

        if (data.success) {
            yield put({ type: actionTypes.GET_ROLES_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not fetch roles list.");
            yield put({ type: actionTypes.GET_ROLES_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_ROLES_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* createRoleSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { formData } = payload;

        const { data } = yield API.post("/roles", formData);

        if (data.success) {
            yield put({ type: actionTypes.CREATE_ROLE_SUCCESS, payload: data.data });
            yield put({ type: actionTypes.GET_ROLES, params: {} });
        } else {
            toast.error(data?.message || "Could not create the role.");
            yield put({ type: actionTypes.CREATE_ROLE_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not create the role.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.CREATE_ROLE_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* updateRoleSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        const { id, formData } = payload;
        const { data } = yield API.put(`/roles/${id}`, formData);
        if (data.success) {
            const { pagination } = yield select((state) => state.Roles.RolesList);
            const params = { currentPage: pagination.currentPage, perPage: pagination.perPage };
            yield put({ type: actionTypes.UPDATE_ROLE_SUCCESS, payload: data.data });
            yield put({ type: actionTypes.GET_ROLES, params });
        } else {
            toast.error(data?.message || "Could not save updated role data.");
            yield put({ type: actionTypes.UPDATE_ROLE_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not save updated role data.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.UPDATE_ROLE_FAILURE, payload: data || { message: "Internal server error" } });
    }
}

function* updateRoleStatusSaga({ Id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { data } = yield API.patch(`/roles/${Id}/status`);

        if (data.success) {
            const { pagination } = yield select((state) => state.Roles.RolesList);
            const params = { currentPage: pagination.currentPage, perPage: pagination.perPage };
            yield put({ type: actionTypes.UPDATE_ROLE_STATUS_SUCCESS, payload: data.data });
            yield put({ type: actionTypes.GET_ROLES, params });
        } else {
            toast.error(data?.message || "Could not update role status.");
            yield put({ type: actionTypes.UPDATE_ROLE_STATUS_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not update role status.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.CREATE_ROLE_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* deleteRoleSaga({ Id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { data } = yield API.delete(`/roles/${Id}`);

        if (data.success) {
            const { pagination } = yield select((state) => state.Roles.RolesList);
            const params = { currentPage: pagination.currentPage, perPage: pagination.perPage };
            yield put({ type: actionTypes.DELETE_ROLE_SUCCESS, payload: data.data });
            yield put({ type: actionTypes.GET_ROLES, params });
        } else {
            toast.error(data?.message || "Could not delete the role.");
            yield put({ type: actionTypes.DELETE_ROLE_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not delete the role.");
        const data = error?.response?.data;
        toast.error(data?.message ||"Could not delete the role.");
        yield put({ type: actionTypes.DELETE_ROLE_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* getRoleDataSaga({ key }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { data } = yield API.get(`/roles/${key}`);

        if (data.success) {
            yield put({ type: actionTypes.GET_ROLE_DATA_SUCCESS, payload: data.data });
        } else {
            toast.error(data?.message || "Could not load role permissions.");
            yield put({ type: actionTypes.GET_ROLE_DATA_FAILURE, payload: data });
        }

    } catch (error) {
        const data = error?.response?.data;
        if(data.status === 404) yield put({ type: appActionTypes.SET_SHOW_NOT_FOUND_PAGE, payload: true });
        // handleApiError(error, "Oops! Something went unexpectedly wrong.");
        yield put({ type: actionTypes.GET_ROLE_DATA_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* getAllPermissionsSaga() {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { data } = yield API.get(`/permissions`);

        if (data.success) {
            yield put({ type: actionTypes.GET_ALL_PERMISSIONS_SUCCESS, payload: data.data });
        } else {
            toast.error(data?.message || "Could not load permissions.");
            yield put({ type: actionTypes.GET_ALL_PERMISSIONS_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not load permissions.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_ALL_PERMISSIONS_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* updateRolePermissionsSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { role, permissions } = payload;

        const { data } = yield API.post(`/role-permissions/${role.id}`, { permissions });

        if (data.success) {
            toast.success("Role permissions updated successfully.");
            yield put({ type: actionTypes.UPDATE_ROLE_PERMISSIONS_SUCCESS, payload: data.data });
            yield put({ type: actionTypes.GET_ROLE_DATA, key: role.key });
        } else {
            toast.error(data?.message || "Could not save role permissions.");
            yield put({ type: actionTypes.UPDATE_ROLE_PERMISSIONS_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not save role permissions.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.UPDATE_ROLE_PERMISSIONS_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* RolesSaga() {
  yield all([
    takeLatest(actionTypes.GET_ROLES, getRolesSaga),
    takeLatest(actionTypes.CREATE_ROLE, createRoleSaga),
    takeLatest(actionTypes.UPDATE_ROLE, updateRoleSaga),
    takeLatest(actionTypes.UPDATE_ROLE_STATUS, updateRoleStatusSaga),
    takeLatest(actionTypes.DELETE_ROLE, deleteRoleSaga),
    takeLatest(actionTypes.GET_ROLE_DATA, getRoleDataSaga),
    takeLatest(actionTypes.GET_ALL_PERMISSIONS, getAllPermissionsSaga),
    takeLatest(actionTypes.UPDATE_ROLE_PERMISSIONS, updateRolePermissionsSaga),
  ]);
}

export default RolesSaga;
